import React, { useState, useRef, useEffect, useMemo } from "react";
import Map, { Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import wkt from "wellknown";
import bbox from "@turf/bbox";
import ModalAddDataFile from "../ModalAddDataFile";
import ModalAddDataURL from "../ModalAddDataURL";
import { defaultOpenDataSources } from "../../constants/formOptions";

function StepExistingData({ formData, updateFormData }) {
  const [isAddDataFileModalOpen, setIsAddDataFileModalOpen] = useState(false);
  const [isAddDataURLModalOpen, setIsAddDataURLModalOpen] = useState(false);
  const mapRef = useRef();
  const [viewport, setViewport] = useState({
    latitude: 41.8719,
    longitude: 12.56,
    zoom: 10,
  });

  const addDataFiles = (dataFile) => {
    updateFormData("dataFiles", [...formData.dataFiles, dataFile]);
  };

  const removeDataFiles = (id) => {
    //TODO: send delete shape datafile to server
    updateFormData("dataFiles", formData.dataFiles.filter((item) => item._id !== id));
  };

  const addDataURL = (dataURL) => {
    updateFormData("dataURLs", [...formData.dataURLs, dataURL]);
  };

  const removeDataURL = (id) => {
    updateFormData("dataURLs", formData.dataURLs.filter((item) => item._id !== id));
  };

  const [newSource, setNewSource] = useState("");

  const handleOpenDataSourceChange = (source) => {
    const updatedSources = formData.openDataSources.includes(source)
      ? formData.openDataSources.filter((item) => item !== source)
      : [...formData.openDataSources, source];

    updateFormData("openDataSources", updatedSources);
  };

  const handleAddNewSource = () => {
    if (newSource.trim() && !formData.openDataSources.includes(newSource)) {
      updateFormData("openDataSources", [...formData.openDataSources, newSource]);
      setNewSource("");
    }
  };

  const dataFileBounds = useMemo(() => {
    const dataFiles = formData?.dataFiles || [];

    const extentGeoms = dataFiles
      .map(d => d.shape?.layer?.extent)
      .filter(Boolean)
      .map(extentWKT => wkt.parse(extentWKT));

    if (extentGeoms.length === 0) return null;

    const featureCollection = {
      type: "FeatureCollection",
      features: extentGeoms.map(geom => ({
        type: "Feature",
        geometry: geom,
      })),
    };

    return bbox(featureCollection);
  }, [formData?.dataFiles]);

  useEffect(() => {
    if (mapRef.current && dataFileBounds) {
      mapRef.current.fitBounds(dataFileBounds, { padding: 20 });
    }
  }, [dataFileBounds]);

  return (
    <div>
      <h3>Existing Data</h3>
      <div className="row-flex">
        <div>
          <div className="bubble" >
            <p>Are there existing data for the area?</p>
            <label>
              <input
                type="radio"
                name="existingData"
                value="yes"
                checked={formData.existingData === "yes"}
                onChange={() => updateFormData("existingData", "yes")}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="existingData"
                value="no"
                checked={formData.existingData === "no"}
                onChange={() => updateFormData("existingData", "no")}
              />
              No
            </label>
            {formData.existingData === "yes" && (
              <>
                <ul className="item-list">
                  {formData.dataFiles.map((item, index) => (
                    <li key={index} style={{ backgroundColor: '#F2F3F7', border: 'none' }}>
                      {item.name}{" "}
                      <span>{item.type}</span>
                      <i className='icon-remove' style={{ float: 'right' }} onClick={() => removeDataFiles(item._id)}></i>
                    </li>
                  ))}
                </ul>
                <button className="action-link" onClick={() => setIsAddDataFileModalOpen(true)}>+ Add a data file</button>
                <ModalAddDataFile projectId={formData.project._id} isOpen={isAddDataFileModalOpen} onClose={() => setIsAddDataFileModalOpen(false)} onDone={addDataFiles} />
              </>
            )}
          </div>

          <div className="bubble">
            <div style={{ marginTop: "20px" }}>
              <p>Do you want to integrate one or more of our company's data sources?</p>
              <label>
                <input
                  type="radio"
                  name="openDataIntegration"
                  value="yes"
                  checked={formData.openDataIntegration === "yes"}
                  onChange={() => updateFormData("openDataIntegration", "yes")}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="openDataIntegration"
                  value="no"
                  checked={formData.openDataIntegration === "no"}
                  onChange={() => updateFormData("openDataIntegration", "no")}
                />
                No
              </label>
            </div>

            {formData.openDataIntegration === "yes" && (
              <div style={{ marginTop: "20px" }}>
                <p>Select Open Data Sources:</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", flexDirection: "column" }}>
                  {defaultOpenDataSources.map((source) => (
                    <label key={source} style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={!["Pluviometri", "MOISTURE INDEX", "NDWI", "NDVI", "SWIR", "False-color-urban",].includes(source)/*formData.openDataSources.includes(source)*/}
                        disabled={true}
                        onChange={() => handleOpenDataSourceChange(source)}
                        style={{ marginRight: "10px" }}
                      />
                      {source}
                    </label>
                  ))}
                  {formData.openDataSources
                    .filter((source) => !defaultOpenDataSources.includes(source))
                    .map((source) => (
                      <label key={source} style={{ display: "flex", alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={formData.openDataSources.includes(source)}
                          onChange={() => handleOpenDataSourceChange(source)}
                          style={{ marginRight: "10px" }}
                        />
                        {source}
                      </label>
                    ))}
                </div>

                <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
                  <input
                    type="text"
                    value={newSource}
                    onChange={(e) => setNewSource(e.target.value)}
                    placeholder="Add a new source"
                  />
                  <button type="button" onClick={handleAddNewSource} style={{ padding: "5px 10px" }}>
                    +
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bubble">
            <div style={{ marginTop: "20px" }}>
              <p>Do you want to integrate one or more third party data-sources?</p>
              <label>
                <input
                  type="radio"
                  name="thirdPartyFiles"
                  value="yes"
                  checked={formData.thirdPartyFiles === "yes"}
                  onChange={() => updateFormData("thirdPartyFiles", "yes")}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="thirdPartyFiles"
                  value="no"
                  checked={formData.thirdPartyFiles === "no"}
                  onChange={() => updateFormData("thirdPartyFiles", "no")}
                />
                No
              </label>
              {formData.thirdPartyFiles === "yes" && (
                <>
                  <ul className="item-list">
                    {formData.dataURLs.map((item, index) => (
                      <li key={index}><i className='icon-remove' onClick={() => removeDataURL(item._id)}></i> {item.name} <span>{item.type}</span></li>
                    ))}
                  </ul>
                  <button className="action-link" onClick={() => setIsAddDataURLModalOpen(true)}>+ Add a data URL</button>
                  <ModalAddDataURL isOpen={isAddDataURLModalOpen} onClose={() => setIsAddDataURLModalOpen(false)} onDone={addDataURL} />
                </>
              )}
            </div>
          </div>
        </div>
        <div className="map-wrapper">
          <Map
            reuseMaps
            ref={mapRef}
            className="area-map"
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            initialViewState={viewport}
            mapStyle="mapbox://styles/mapbox/streets-v11"
            onMove={(evt) => setViewport(evt.viewState)}
          >
            {formData?.dataFiles.map(s => {
              if (!s.shape?._id) return null;
              const layerId = `layer-${s.type}-${s.shape._id}`;
              return <Source key={s.shape?._id} type="vector" url={`mapbox://${s.shape?.layer?.source_id}`}>
                <Layer {...{
                  id: layerId,
                  'source-layer': s.shape?.layer?.source_layer,
                  type: 'fill',
                  paint: {
                    'fill-color': '#010E2D',
                    'fill-opacity': 0.5,
                  },
                }} />
              </Source>;
            })}
          </Map>
        </div>
      </div>
    </div>
  );
}

export default StepExistingData;
